import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["block", "children", "disabled", "flush", "height", "name", "onClick", "onSelectedChange", "padded", "readOnly", "selected", "selectionMarkLocation", "tabIndex", "type", "width"];
import devLogger from 'react-utils/devLogger';
import { useMemo, useCallback } from 'react';
import { Controllable } from '../Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { Checkbox } from '../Checkbox';
import { RadioInput } from '../RadioInput';
import { uniqueId } from '../../utils/uniqueId';
import { stopPropagationHandler } from '../../utils/DomEvents';
import { useActive } from '../../utils/useActive';
import { useHovered } from '../../utils/useHovered';
import styled from 'foundations-theming/utils';
import { fwcColor } from 'foundations-theming/utils/fwc';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useSelectableButtonContext } from '../SelectableButton/SelectableButtonProvider';
import { deepMerge } from '../../utils/deepMerge';
import { TRANSITION_ALL_STYLE } from '../../../constants/transition-all';
import { getFocusStyleObject } from '../../utils/focus';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const StyledSelectableBox = styled.div.withConfig({
  displayName: "SelectableBox__StyledSelectableBox"
})(({
  theme,
  $cssStyles,
  $block,
  $disabled,
  $flush,
  $hovered,
  $padded,
  $readOnly,
  $selected,
  $active,
  $styleOverrides
}) => {
  const baseStyles = Object.assign({
    backgroundColor: theme.color['fill-surface-default'],
    borderWidth: theme.borderWidth['100'],
    borderStyle: 'solid',
    borderColor: theme.color[fwcColor('fwc-selectable-border-default')],
    borderRadius: theme.borderRadius['200'],
    cursor: 'pointer',
    display: 'inline-block',
    position: 'relative',
    userSelect: 'none',
    width: 'auto',
    textAlign: 'center',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    transition: TRANSITION_ALL_STYLE
  }, $block && {
    width: '100%'
  }, $padded && {
    padding: theme.space['500']
  }, $hovered && !$disabled && !$readOnly && {
    boxShadow: theme.boxShadow['300']
  }, !$disabled && !$readOnly && Object.assign({}, getFocusStyleObject({
    theme
  })), ($active || $selected) && {
    backgroundColor: theme.color['fill-field-hover']
  }, $selected && {
    borderColor: theme.color[fwcColor('fwc-selectable-border-selected')]
  }, $selected && !$disabled && !$readOnly && Object.assign({
    boxShadow: theme.boxShadow['300'],
    '&:hover': {
      boxShadow: theme.boxShadow['300']
    }
  }, $hovered && {
    boxShadow: theme.boxShadow['300']
  }), $readOnly && {
    backgroundColor: `${theme.color['fill-primary-disabled']} !important`,
    cursor: 'auto',
    '& input + span': {
      borderColor: `${theme.color['border-core-default']} !important`
    }
  }, ($readOnly || $disabled) && !$selected && {
    borderColor: `${theme.color['border-core-subtle']} !important`
  }, $disabled && {
    backgroundColor: `${theme.color['fill-primary-disabled']} !important`,
    color: `${theme.color['text-core-disabled']} !important`,
    cursor: 'not-allowed'
  }, $flush && {
    padding: `${theme.space['0']} !important`
  });
  return deepMerge(baseStyles, $styleOverrides || {}, $cssStyles);
});
export const StyledInnerBox = styled.div.withConfig({
  displayName: "SelectableBox__StyledInnerBox"
})(({
  theme,
  $height,
  $width
}) => {
  return {
    fontSize: theme.typography['body-200'].fontSize,
    marginLeft: 0,
    whiteSpace: 'normal',
    height: $height ? `${$height}px` : 'auto',
    width: $width ? `${$width}px` : 'auto'
  };
});
const StyledSelectionMark = styled.div.withConfig({
  displayName: "SelectableBox__StyledSelectionMark"
})(({
  theme,
  $location
}) => {
  return Object.assign({
    position: 'absolute'
  }, $location === 'top-right' && {
    right: theme.space[300],
    top: theme.space[400]
  }, $location === 'left' && {
    left: theme.space[500],
    marginTop: `calc(-1 * ${theme.space[250]})`,
    top: '50%',
    [`& + ${StyledInnerBox}`]: {
      paddingLeft: theme.space[600]
    }
  });
});
const InternalSelectableBox = props => {
  const {
      block = false,
      children,
      disabled = false,
      flush = false,
      height,
      name,
      onClick,
      onSelectedChange,
      padded = true,
      readOnly = false,
      selected = false,
      selectionMarkLocation,
      tabIndex,
      type = 'default',
      width
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    selectableButtonStyleOverrides
  } = useSelectableButtonContext() || {};
  const {
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const _id = useMemo(() => uniqueId('bsb-'), []);
  const handleClick = useCallback(evt => {
    if ((disabled || readOnly) && evt != null) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }
    if (type !== 'radio') {
      onSelectedChange === null || onSelectedChange === void 0 || onSelectedChange(syntheticEvent(!selected));
    }
    if (onClick && !disabled) onClick(evt);
  }, [disabled, readOnly, type, selected, onSelectedChange, onClick]);
  const handleKeyPress = useCallback(evt => {
    if (evt.currentTarget.getAttribute('type') === 'checkbox') return;
    const {
      key
    } = evt;
    if ((key === 'Enter' || key === ' ') && !disabled) {
      evt.preventDefault();
      handleClick();
    }
  }, [disabled, handleClick]);
  const activeProps = useActive(props);
  const hoveredProps = useHovered(props);
  let renderedSelectionMark = null;
  if (type !== 'default') {
    if (type === 'checkbox') {
      renderedSelectionMark = /*#__PURE__*/_jsx(StyledSelectionMark, {
        $location: selectionMarkLocation,
        children: /*#__PURE__*/_jsx(Checkbox, {
          "aria-labelledby": _id,
          checked: selected,
          disabled: disabled,
          innerPadding: "none",
          name: name,
          onChange: handleClick,
          onClick: stopPropagationHandler,
          readOnly: readOnly
        })
      });
    }
    if (type === 'radio') {
      if (process.env.NODE_ENV !== 'production' && !name) {
        devLogger.warn({
          message: 'SelectableBox: If type is set to radio you must provide a name',
          key: 'SelectableBox: no name value'
        });
      }
      renderedSelectionMark = /*#__PURE__*/_jsx(StyledSelectionMark, {
        $location: selectionMarkLocation,
        children: /*#__PURE__*/_jsx(RadioInput, {
          "aria-labelledby": _id,
          checked: selected,
          disabled: disabled,
          name: name,
          onChange: handleClick,
          onClick: stopPropagationHandler,
          readOnly: readOnly
        })
      });
    }
  }
  const tabbableByDefault = !disabled && type === 'default';
  const defaultTabIndex = tabbableByDefault ? 0 : undefined;
  return /*#__PURE__*/_jsxs(StyledSelectableBox, Object.assign({}, rest, activeProps, hoveredProps, {
    $cssStyles: cssStyles,
    $block: block,
    $disabled: disabled,
    $flush: flush,
    $hovered: hoveredProps.hovered,
    $padded: padded,
    $readOnly: readOnly,
    $selected: selected,
    $active: activeProps.active,
    $styleOverrides: selectableButtonStyleOverrides,
    "aria-checked": selected,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    onClick: handleClick,
    onKeyPress: handleKeyPress,
    role: type === 'radio' ? 'radio' : 'checkbox',
    tabIndex: typeof tabIndex === 'number' ? tabIndex : defaultTabIndex,
    children: [renderedSelectionMark, /*#__PURE__*/_jsx(StyledInnerBox, {
      id: _id,
      $height: height,
      $width: width,
      children: children
    })]
  }));
};
const defaultProps = {
  selected: false
};
export const SelectableBox = Controllable(InternalSelectableBox, ['selected'], defaultProps);
SelectableBox.displayName = 'SelectableBox';